import React, { FC, useEffect, useState } from 'react';
import JsonView from '@uiw/react-json-view';
import JsonViewEditor from '@uiw/react-json-view/editor';
import axios from 'axios';

import Pax from '@nabancard/pos-cardreader/utils/pax';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { LoaderIconAnimation } from 'Src/components/icons/LoaderIconAnimation';
import { Flex } from 'Src/components/layout/Flex';
import { useCardReader } from 'Src/feature/card-reader/useCardReader';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { FormField } from 'Src/feature/form/FormField';
import { FormFieldSelectProps } from 'Src/feature/form/FormFieldSelect';
import { API_BASE_URL, API_TOOLS_URL } from 'Src/stores/appEnv';
import { Logger } from 'Src/utils/Logger';

import { apiCallOptions } from './apiCallOptions';

import styles from './EPXTool.module.scss';

const apiToolsURL = API_TOOLS_URL || API_BASE_URL;

interface Data {
  [key: string]: unknown;
}

type APICallOptionData = typeof apiCallOptions[number]['data'];

const apiCall = async (URL: string, data: Data, log: Logger) => {
  log.info(`[${new Date().toTimeString()}] calling endpoint ${apiToolsURL}${URL}`);
  return axios.post(`${apiToolsURL}${URL}`, data);
};

const EPXTool: FC<React.PropsWithChildren> = () => {
  // The Pax card reader requires that there only be a single client connected at a time,
  // due to each USB packet having a sequential ID. Therefore, the EPX tool has to connect
  // and communicate with the D135 via the cardReaderService to preserve the sequential
  // IDs.
  const cardReader = useCardReader();
  const log = useLogger().getSubLogger({ name: 'EPXTool' });

  const initialValues = {
    selectedIndex: 0,
    checkId: 0,
  };

  const [formValues, setFormValues] = useState(initialValues);
  const apiCallOption = apiCallOptions[formValues.selectedIndex];
  const [requestBody, setRequestBody] = useState<APICallOptionData>(apiCallOption.data);
  const [response, setResponse] = useState({});
  const [isLoading, setIsloading] = useState(false);
  const [isCardReader, setIsCardReader] = useState(false);
  const [deviceConnected, setDeviceConnected] = useState(false);
  const [displayMessage, setDisplayMessage] = useState('');
  const [pax, setPax] = useState<Pax>();

  const [amount, setAmount] = useState<number>(10);
  const [tip, setTip] = useState<number>(0);
  const [paymentTypeId, setPaymentTypeId] = useState<number>(483);
  const [locationId, setLocationId] = useState<number>(741);

  const handleInputChange: FormFieldSelectProps['onChange'] = (_evt, values) => {
    setFormValues({
      ...formValues,
      selectedIndex: values[0] as number,
    });
  };

  const initiateCardEmv = async () => {
    if (deviceConnected) {
      try {
        // This will connect the reader and initiate EMV, and
        // this will block for up to 30 seconds so may want to change this eventually.
        await cardReader.init({
          mode: 'emv',
          amount: 10,
          onSuccess: (read) => {
            if (read?.pax) {
              setPax(read.pax);
            }
          },
        });
      } catch (err) {
        log.error(err, 'Error during Start Transaction Command');
        // Error Condition
        setDisplayMessage(`Error during Start Transaction Command`);
      }
    }
  };

  const connectDisconnect = async () => {
    if (deviceConnected) {
      // Fake the disconnect because we need the sequential packet IDs
      setDeviceConnected(false);
    } else {
      await cardReader.connect();
      setDeviceConnected(true);
    }
  };

  useEffect(() => {
    Object.assign(apiCallOption.data, {
      locId: locationId,
      paymentTypeId: paymentTypeId,
      total: amount,
      tip: tip,
    });
    setRequestBody(apiCallOption.data);
    if (pax !== undefined) {
      setRequestBody({
        locId: locationId,
        paymentTypeId: paymentTypeId,
        total: amount,
        tip: tip,
        cardReaderType: 'pax',
        paxData: pax,
      } as unknown as APICallOptionData);
    }
  }, [apiCallOption.data, pax, formValues.selectedIndex, paymentTypeId, amount, tip, locationId]);

  return (
    <div className={styles['root']}>
      <Flex>
        <Copy variant="h6" value="EPX Tools" />
        <FormField
          type="select"
          options={apiCallOptions.map((option, index) => ({ value: index, label: option.URL }))}
          value={formValues.selectedIndex}
          onChange={handleInputChange}
          fullWidth
        />
        <CTA
          disabled={isLoading}
          onClick={() => {
            setIsCardReader(!isCardReader);
            setPax(undefined);
          }}
          label={isCardReader ? 'Use Text Editor' : 'Use Card Reader'}
        />

        <FormField
          type="text"
          label="paymentTypeId"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          value={paymentTypeId}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const val = parseInt(event.target.value, 10);
            if (val) {
              setPaymentTypeId(val);
            }
          }}
        />

        <FormField
          type="text"
          label="locationId"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          value={locationId}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const val = parseInt(event.target.value);
            if (val) {
              setLocationId(val);
            }
          }}
        />
        <FormField
          type="text"
          label="amount"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          value={amount}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const val = parseInt(event.target.value, 10);
            if (val) {
              setAmount(val);
            }
          }}
        />
        <FormField
          type="text"
          label="tip"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          value={tip}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            const val = parseInt(event.target.value);
            if (val) {
              setTip(val);
              setAmount(val + amount);
            }
          }}
        />


        <CTA
          disabled={isLoading}
          onClick={() => {
            setIsloading(true);
            void apiCall(apiCallOption.URL, requestBody, log).then((res) => {
              if (res.status === 200) {
                const responseData = res.data as typeof apiCallOption;
                setResponse(responseData.data);
              }
            }).finally(()=>{
              setIsloading(false);
            });
          }}
          icon={{ jsx: isLoading ? <LoaderIconAnimation /> : undefined }}
          label="Send Request to EPX"
        />


        { !isCardReader && (
          <Flex>
            <Copy variant="h5" value="Request" />
            <div className={styles['json-view-wrapper']}>
              <JsonViewEditor
                value={requestBody} // data to display
                onEdit={(option) => {
                  setRequestBody(option.value as APICallOptionData);
                }}
              />
            </div>
          </Flex>
        )}

        { isCardReader && !apiCallOption.URL.includes('bric') && (
          <Flex>
            <CTA
              disabled={isLoading}
              onClick={() => void connectDisconnect()}
              label={deviceConnected ? 'Connected' : 'Scan For Devices'}
            />

            <CTA
              disabled={!deviceConnected}
              onClick={() => void initiateCardEmv()}
              label="Initiate EMV"
            />

            <Copy value={displayMessage}/>

            <div className={styles['json-view-wrapper']}>
              <JsonViewEditor
                value={requestBody} // data to display
                onEdit={(option) => {
                  setRequestBody(option.value as APICallOptionData);
                }}
              />
            </div>
          </Flex>
        )}
        <Copy variant="h5" value="Response" />
        <div className={styles['response']}>
          {isLoading
            ? <Copy value="Loading..." />
            : (
              <div className={styles['json-view-wrapper']}>
                <JsonView value={response} />
              </div>
            )
          }
        </div>
      </Flex>
    </div>
  );
};

export default EPXTool;
